import { z } from 'zod'

import { AudienceObjectSchema } from './search/types'
import { SUPER_NON_SEMANTIC_SOURCES, SuperSource } from './super'

export const DEFAULT_PROGRAM_FREQUENCY_DAYS = 7
export const DEFAULT_SCREENING_WINDOW_DAYS = 180

// Only external sources supported by progressiveSearch can be declared.
export const DECLARABLE_SOURCE_TYPES = Object.values(SuperSource).filter(
  (source) =>
    source !== SuperSource.SLITE && !SUPER_NON_SEMANTIC_SOURCES.includes(source)
)

/** The one validation both the MCP tool and the search controller use. */
export const DeclarableSourceTypeSchema = z.enum(
  DECLARABLE_SOURCE_TYPES as [SuperSource, ...SuperSource[]]
)

// Soft targets: the kernel's respond-only wrap-up triggers here; its hard
// sanity stop is 1.5×.
export const MAINTAINER_RUN_MAX_COST_USD = 10
export const PROMOTED_THREAD_MAX_COST_USD = 1.5

export interface MaintenanceRoutine {
  key: string
  directive?: string
}

export const CUSTOM_ROUTINE_KEY = 'custom'

export const MAINTENANCE_DIRECTIVE_MAX_CHARS = 12_000

export const MAINTENANCE_PROGRAM_NAME_MAX_CHARS = 120

export function ensureOwnerAssignee<T extends { kind: string; id: string }>(
  assignees: T[],
  ownerId: string | null
): (T | { kind: 'user'; id: string })[] {
  if (!ownerId) return assignees
  const hasOwner = assignees.some(
    (assignee) => assignee.kind === 'user' && assignee.id === ownerId
  )
  return hasOwner ? assignees : [{ kind: 'user', id: ownerId }, ...assignees]
}

export const MaintenanceThemeSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
})
export type MaintenanceTheme = z.infer<typeof MaintenanceThemeSchema>

export const MaintenanceSourceParentSchema = z.object({
  id: z.string().min(1),
  sourceType: z.enum(SuperSource),
  title: z.string(),
})
export type MaintenanceSourceParent = z.infer<
  typeof MaintenanceSourceParentSchema
>

const ProgramSourceSchema = z.object({
  kind: z.enum(['sourceType', 'root', 'note']),
  value: z.string().min(1),
})

export const MembersMissingAccessRequestSchema = z.object({
  organizationId: z.string().min(1),
  ownerAudience: AudienceObjectSchema,
  members: z.array(
    z.object({ userId: z.string().min(1), audience: AudienceObjectSchema })
  ),
  sources: z.array(ProgramSourceSchema),
})
export type MembersMissingAccessRequest = z.infer<
  typeof MembersMissingAccessRequestSchema
>

export type MembersMissingAccess = z.infer<typeof ProgramSourceSchema> & {
  /** Read some of what the owner reads in the source, not all of it. */
  userIdsWithPartialAccess: string[]
  /** Read none of what the owner reads in the source. */
  userIdsWithoutAccess: string[]
}

export const MaintenanceReviewerSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  email: z.string(),
})
export type MaintenanceReviewer = z.infer<typeof MaintenanceReviewerSchema>

const READ_THE_CODE_FIRST = `When a repository is declared, read its code before stating what the product does or shows, and before reporting that the sources leave a claim unsettled: the code is what the product does today, where an announcement, a ticket or a pull request description says what someone meant it to.`

const CHECK_EACH_DOC_ALONE = `A relied-upon doc checked without a change behind it gets an assignment of its own, since it is read whole against its sources; a landed change stays one assignment however many docs it touches.`

const FACT_CHECK_WHOLE_DOC = `Fact-check the whole doc against the sources: every claim, the people it names included; what it leaves out that a reader needs, when a source the team keeps current on the same subject states it, such as the instructions a repository keeps for its own code; and the parts that describe a state of things that is over. Propose the edits that make it right, up to rewriting the whole body from those sources, and propose Verified with them, so one review leaves the doc right and verified; when the sources confirm the doc as it stands, propose Verified alone. A claim the sources neither confirm nor contradict stays as written and does not hold back Verified: name it in the finding, for the reviewer to judge.`

const MAINTENANCE_DIRECTIVE = `Keep the docs in this scope true, current and worth keeping. Work from what the brief gives you: the topics and candidates found by screening the scope when it lists any, otherwise the sweep of each declared source over the window, and the docs themselves.

What to fix:
- A claim a landed change made false: a merged pull request, a completed ticket, a decision, an announcement, a confirmed support answer, or a newer doc. Open or draft work, planned work, and changes behind a flag or restricted to development have not landed yet.
- Two docs that state incompatible things about the same fact: fix the wrong one only when evidence outside both docs settles it — a landed change in the sources, a Verified doc, or an evidence doc the program declares. A newer edit date settles nothing, and neither does one doc being more detailed. Otherwise request verification on both docs with one precise question naming the two values.
- A doc wholly replaced by a newer doc: flag it Outdated and name the replacement. A doc nobody needs anymore, empty, abandoned or about something that no longer exists: propose archiving it, and say why.
- A relied-upon doc whose verification expired or that was never verified. ${FACT_CHECK_WHOLE_DOC}
- A comment thread that asks for a change the doc never got: make the change and answer the thread.

Use judgment about what a doc is for. A process, a reference, a runbook or a how-to is meant to stay true and earns a fix whenever the world has moved past it. A plan, a roadmap, an RFC, a pitch, a post-mortem, a meeting note, a retro, an analysis or a finished project's working doc records what was known or intended when it was written: leave it alone, its inconsistencies included, and fix only a part meant to track the present, such as a status table or a links section.

Remedies, least invasive first: an edit when the sources show what the doc should say, or that a passage no longer applies, rewriting or removing it; Verified with the edits on a relied-upon doc, as above; Outdated when it is superseded; archive when nothing replaces it. A verification request is the last resort: one factual question that the sources and the code cannot settle, naming the values in conflict, so its owner answers a question rather than reviews a doc. It goes to the doc's owner: when the owner has left the workspace, address it to a new owner instead, the active member the evidence shows knows its subject best. Write clean final content, never a warning or a "may be outdated" note. Verified docs are what readers trust without checking: read them first, and correct them only for a landed change.

${READ_THE_CODE_FIRST}

Read a doc in full before changing it. Once a change makes one doc wrong, search the scope for the old name, command, URL or number and fix every living doc that still uses it.

When one run cannot check everything, go in this order: what a landed change made wrong, then relied-upon docs whose verification expired, the most linked first, then the rest; what is left waits for the next run. ${CHECK_EACH_DOC_ALONE}`

export const PRE_SCREENED_MAINTENANCE_ROUTINE_KEY = 'preScreenedMaintenance'

export const CONTRADICTIONS_ROUTINE_KEY = 'contradictions'

export type MaintenanceExecutor = 'maintainer' | 'contradictions'

export type ProgramScope = 'note' | 'organization'

interface RoutineDefinition {
  title: string
  executor: MaintenanceExecutor
  scopes: readonly ProgramScope[]
  needsSources: boolean
  directive?: string
}

const MAINTENANCE_ROUTINE: RoutineDefinition = {
  title: 'Maintenance',
  executor: 'maintainer',
  scopes: ['note'],
  needsSources: true,
  directive: MAINTENANCE_DIRECTIVE,
}

const ROUTINES: Record<string, RoutineDefinition> = {
  maintenance: MAINTENANCE_ROUTINE,
  [PRE_SCREENED_MAINTENANCE_ROUTINE_KEY]: MAINTENANCE_ROUTINE,
  archiveUnused: {
    title: 'Archive unused docs',
    executor: 'maintainer',
    scopes: ['note'],
    needsSources: true,
    directive: `Find the docs in this scope that nobody needs anymore and propose archiving them. A doc qualifies when it is empty or near-empty, long inactive and about something that no longer exists, wholly superseded by a newer doc on the same subject, or the working doc of a finished project that nothing links to. Use the map's dates, the doc's backlinks and its activity before deciding: a doc that is old but still read, linked or referenced is a reference, not clutter. Templates and docs already Outdated or in VerificationRequested are left alone. For a doc superseded by a live doc, prefer Outdated with the replacement named; archive when nothing replaces it. Read a doc in full before proposing anything, and say in the message why it is no longer needed.`,
  },
  toVerify: {
    title: 'Get docs verified',
    executor: 'maintainer',
    scopes: ['note'],
    needsSources: true,
    directive: `Find the canonical docs in this scope that carry no verification and get them verified. A doc qualifies when readers take it as the reference on its subject, still open it or link to it, and it is not Verified, not already in VerificationRequested and not Outdated. Records of a moment do not qualify. ${FACT_CHECK_WHOLE_DOC} ${CHECK_EACH_DOC_ALONE}

${READ_THE_CODE_FIRST}`,
  },
  [CUSTOM_ROUTINE_KEY]: {
    title: 'Custom routine',
    executor: 'maintainer',
    scopes: ['note'],
    needsSources: true,
  },
  [CONTRADICTIONS_ROUTINE_KEY]: {
    title: 'Contradictions',
    executor: 'contradictions',
    scopes: ['organization'],
    needsSources: false,
  },
}

export const WORKSPACE_ROUTINE_KEYS = Object.entries(ROUTINES)
  .filter(([, routine]) => routine.scopes.includes('organization'))
  .map(([key]) => key)

export const AGENT_DIRECTIVE_PRESETS: Record<string, string> =
  Object.fromEntries(
    Object.entries(ROUTINES).flatMap(([key, routine]) =>
      routine.directive ? [[key, routine.directive]] : []
    )
  )

const DIRECTIVE_PRESET_ALIASES: Record<string, string> = {
  factCheck: 'maintenance',
}

/** A routine key with its alias resolved: `factCheck` is `maintenance`. */
export const presetKeyOf = (key: string): string =>
  DIRECTIVE_PRESET_ALIASES[key] ?? key

const findRoutine = (key: string): RoutineDefinition | undefined => {
  const resolved = presetKeyOf(key)
  return Object.hasOwn(ROUTINES, resolved) ? ROUTINES[resolved] : undefined
}

export const isKnownRoutine = (key: string): boolean =>
  findRoutine(key) !== undefined

export const findDirectivePreset = (key: string): string | undefined =>
  findRoutine(key)?.directive

export const routineTitleOf = (routineKeys: readonly string[]): string =>
  routineKeys.map(findRoutine).find((routine) => routine !== undefined)
    ?.title ?? MAINTENANCE_ROUTINE.title

export interface ProgramRequirements {
  executor: MaintenanceExecutor
  scopes: ProgramScope[]
  needsSources: boolean
}

export const programRequirementsOf = (
  routineKeys: readonly string[]
): ProgramRequirements | null => {
  const known = routineKeys.flatMap((key) => {
    const routine = findRoutine(key)
    return routine ? [routine] : []
  })
  const routines = known.length ? known : [MAINTENANCE_ROUTINE]
  const executors = new Set(routines.map((routine) => routine.executor))
  const [executor] = executors
  if (executors.size > 1 || !executor) return null
  return {
    executor,
    scopes: (['note', 'organization'] as const).filter((scope) =>
      routines.every((routine) => routine.scopes.includes(scope))
    ),
    needsSources: routines.some((routine) => routine.needsSources),
  }
}

export const DEFAULT_MAINTENANCE_DIRECTIVE_KEY = 'maintenance'

export const routinesUsePreRunScreening = (
  routineKeys: readonly string[] | undefined
): boolean =>
  (routineKeys ?? []).some(
    (key) => presetKeyOf(key) === PRE_SCREENED_MAINTENANCE_ROUTINE_KEY
  )

/** A maintain run's directive: the text the caller gave, else the default
 *  preset. */
export const resolveMaintenanceDirective = (directive?: string): string => {
  const resolved =
    directive ?? findDirectivePreset(DEFAULT_MAINTENANCE_DIRECTIVE_KEY)
  if (!resolved)
    throw new Error(
      `No directive given and no '${DEFAULT_MAINTENANCE_DIRECTIVE_KEY}' preset`
    )
  return resolved
}

const routineDirective = (routine: MaintenanceRoutine): string | undefined =>
  routine.directive ?? findDirectivePreset(routine.key)

export const composeProgramDirective = (
  routines: MaintenanceRoutine[]
): string => {
  const directives = routines.flatMap((routine) => {
    const directive = routineDirective(routine)
    return directive ? [directive] : []
  })
  return resolveMaintenanceDirective(
    directives.length ? directives.join('\n\n') : undefined
  )
}

function declaredSourcesSections({
  frequencyInDays,
  windowDays,
  sourceTypes,
  sourceParents,
}: {
  frequencyInDays: number
  windowDays: number
  sourceTypes?: string[]
  sourceParents?: MaintenanceSourceParent[]
}): { evidenceSection: string; sourcesSection: string } {
  const externalParents = (sourceParents ?? []).filter(
    (parent) => parent.sourceType !== SuperSource.SLITE
  )
  const evidenceDocs = (sourceParents ?? []).filter(
    (parent) => parent.sourceType === SuperSource.SLITE
  )
  const sweeps = [
    ...(sourceTypes ?? []).map(
      (sourceType) => `- ${sourceType}: sourceTypes ["${sourceType}"]`
    ),
    ...externalParents.map(
      (parent) =>
        `- ${parent.sourceType} "${parent.title}": parents ["${parent.id}"]`
    ),
  ]
  const evidenceSection = evidenceDocs.length
    ? `\n\nEvidence docs the program declares outside the scope — read them (through a checker, with getNote — they sit outside the file tree) when the directive needs what they say, never act on them: ${evidenceDocs
        .map((doc) => `"${doc.title}" (id: ${doc.id})`)
        .join(', ')}.`
    : ''

  const windowSentence = `Read each one in two parts. The last ${frequencyInDays} days, since the program last ran: listSourceActivity, every row — what is new, and where most findings start. Before that, back to ${windowDays} days: summarizeSourceActivity, one call per line, with readers set to this scope's title — the changes of those months as topics, each saying what is true now; weigh a topic as you would a row, and read its cited rows only to verify a claim a doc makes. A change counts whenever it landed: a doc wrong since months is as wrong as one wrong since yesterday.`
  const sourcesSection = sweeps.length
    ? `Declared sources, for the directive to draw on as it says — read one with exactly the filters on its line, only when the directive works from what changed in it; a directive that works from the docs alone leaves them unread:
${sweeps.join('\n')}
${windowSentence}`
    : `Declared sources: none. This program declares no external sources — do not sweep external sources.`
  return { evidenceSection, sourcesSection }
}

const reviewersSection = (reviewers: MaintenanceReviewer[]): string =>
  reviewers.length
    ? `\n\nReviewers — each thread you promote goes to one of them, the person asked to review its cards. Pass promoteInstance, as assignee, the id of the reviewer who knows that thread's subject best, judged from what its checker read and reported, and as assigneeReason what the evidence shows they did on that subject; knowledge decides, not workload. When the checker shows that someone outside them knows the subject better than any of them, pass that person as suggestedReviewer too — the reviewers decide whether to hand them the thread:\n${reviewers
        .map(
          (reviewer) =>
            `- ${reviewer.name} <${reviewer.email}> (id: ${reviewer.id})`
        )
        .join('\n')}`
    : ''

const memorySection = (memory: string[]): string =>
  memory.length
    ? `\n\nWhat this program's previous runs proposed (most recent first):\n\n${memory.join('\n\n')}\n\nUse this memory to judge repeats: a change that was already proposed and not applied was seen and set aside by the team — only come back to that doc with substantially different, newer evidence.`
    : ''

export function buildMaintainBrief({
  directive,
  playbook,
  scopeNoteId,
  scopeTitle,
  frequencyInDays,
  windowDays = DEFAULT_SCREENING_WINDOW_DAYS,
  memory,
  sourceTypes,
  sourceParents,
  screening,
  reviewers = [],
}: {
  directive: string
  /** How to carry out the directive's routine, when it has a playbook. */
  playbook?: string
  scopeNoteId: string
  scopeTitle: string | null
  frequencyInDays: number
  /** How far back the declared sources are read. */
  windowDays?: number
  memory: string[]
  sourceTypes?: string[]
  sourceParents?: MaintenanceSourceParent[]
  screening?: string
  reviewers?: MaintenanceReviewer[]
}): string {
  const { evidenceSection, sourcesSection } = declaredSourcesSections({
    frequencyInDays,
    windowDays,
    sourceTypes,
    sourceParents,
  })

  return `Maintain the knowledge base under "${scopeTitle ?? scopeNoteId}" (id: ${scopeNoteId}). The file tree rooted at / is that doc and its sub-docs: only docs in it qualify, and every change you propose targets one of them.

Directive — what this run looks for, what it changes and what evidence that takes. The run does what it says and nothing else; a doc that matches nothing in it is left alone:
${directive}
${playbook ? `\nHow to carry out this routine:\n${playbook}\n` : ''}
${sourcesSection}${evidenceSection}${screening ? `\n\n${screening}` : ''}

Every change is proposed as an approval card a human reviews; nothing is applied without approval. Propose Verified only where the directive calls for it, and never assign people to a doc.${reviewersSection(reviewers)}${memorySection(memory)}

Final response: call respond with the run's report — a title, a summary of what the run found and why the docs needed work, and one entry per promoted thread.`
}

/** One maintainer finds, checks and promotes in one run; the promoted
 *  threads are what the reviewer sees. */
export interface MaintenanceRunRequest {
  scopeNoteId: string
  /** The whole routine as one text; omit for the default preset. */
  directive?: string
  /** The routines the directive was composed from, when a program's. */
  routineKeys?: string[]
  frequencyInDays?: number
  screeningWindowDays?: number
  sourceTypes?: string[]
  sourceParents?: MaintenanceSourceParent[]
  memory?: string[]
  /** Model profile ID; omit for the maintainer's default loadout. */
  modelProfile?: string
  /** Screen the whole scope with Jev before the maintainer round. */
  preRunScreening?: boolean
  reviewers?: MaintenanceReviewer[]
  /** The members besides the owner: the run reads only what all of them can
   *  read too. */
  audienceUserIds?: string[]
}
